import { useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  emailBodySchema,
  emailDraftSchema,
  type EmailTemplateCopy,
  type TemplateAction,
  type TemplateVariable,
} from '@duke-hq/email-templates';
import { useDebouncedCallback } from 'use-debounce';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { emailEditorExtensions } from './emailEditorExtensions';
import { CtaInsert } from './CtaInsert';
import { EmailFormatToolbar } from './EmailFormatToolbar';
import { VariablePicker } from './VariablePicker';
import { testIds } from './testIds';

/**
 * Editing one email's wording. Saving is rejected outright rather than warned
 * about, and the reasons come from the server: the rules the sender would hit.
 */
export function TemplateEditor({
  copy,
  variables,
  fragment,
  action,
  saving,
  problem,
  onSave,
  onDraftChange,
  onCancel,
}: {
  copy: EmailTemplateCopy;
  variables: TemplateVariable[];
  /** The block this template's send path supplies, when it has one. */
  fragment?: { name: string; label: string };
  /** The button this template's send path supplies, when it has one. */
  action?: TemplateAction;
  saving: boolean;
  problem?: string;
  onSave: (copy: EmailTemplateCopy) => void;
  onDraftChange: (copy: EmailTemplateCopy) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [subject, setSubject] = useState(copy.subject);
  const [previewText, setPreviewText] = useState(copy.previewText);
  const [body, setBody] = useState(copy.body);
  const [bodyProblem, setBodyProblem] = useState<string>();

  // The preview is rendered on the server, so a round trip per keystroke would be
  // slow and pointless.
  const publishDraft = useDebouncedCallback(onDraftChange, 400);

  useEffect(() => {
    publishDraft({ subject, previewText, body });
  }, [subject, previewText, body, publishDraft]);

  const editor = useEditor({
    extensions: emailEditorExtensions,
    content: copy.body,
    onUpdate: ({ editor: current }) => {
      // The draft shape, not the one saving demands: a body emptied out mid
      // rewrite must still reach the preview, or it freezes on the old wording
      // while the author watches. Saving is where a body that says nothing is
      // refused.
      const parsed = emailDraftSchema.safeParse(current.getJSON());
      if (parsed.success) {
        setBody(parsed.data);
      }
    },
  });

  const save = (): void => {
    const parsed = emailBodySchema.safeParse(editor?.getJSON());
    if (!parsed.success) {
      // Reachable: the editor's nodes are the schema's, but this is the schema
      // saving applies, and it refuses a body that says nothing -- which is what
      // is on screen after emptying one out. Its own message says which rule was
      // broken; a generic one would leave the author guessing why nothing
      // happened.
      setBodyProblem(
        parsed.error.issues[0]?.message ?? t`This wording cannot be saved as written.`,
      );
      return;
    }
    setBodyProblem(undefined);
    onSave({ subject, previewText, body: parsed.data });
  };

  return (
    <Stack gap={3}>
      <FormControl>
        <FormLabel fontSize="sm">
          <Trans>Subject</Trans>
        </FormLabel>
        <Input
          size="sm"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid={testIds.subjectInput}
        />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">
          <Trans>Preview text</Trans>
        </FormLabel>
        <Input
          size="sm"
          value={previewText}
          onChange={(e) => setPreviewText(e.target.value)}
          data-testid={testIds.previewTextInput}
        />
      </FormControl>

      <Stack gap={2}>
        <EmailFormatToolbar editor={editor} />
        <Stack direction="row" gap={2} wrap="wrap">
          <VariablePicker editor={editor} variables={variables} />
          {fragment == null ? null : (
            <Button
              size="sm"
              variant="outline"
              isDisabled={editor === null}
              onClick={() =>
                editor
                  ?.chain()
                  .focus()
                  .insertContent({ type: 'fragment', attrs: { name: fragment.name } })
                  .run()
              }
              data-testid={testIds.insertFragment}
            >
              <Trans>Insert {fragment.label}</Trans>
            </Button>
          )}
          {action == null ? null : <CtaInsert editor={editor} action={action} />}
        </Stack>
        <Box
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          p={3}
          minHeight={pxToRem(280)}
          data-testid={testIds.bodyEditor}
          sx={{
            '.ProseMirror': { outline: 'none' },
            // Chakra's reset flattens headings and strips list markers, so
            // without these a body's structure is invisible while it is written.
            '.ProseMirror > * + *': { mt: 3 },
            '.ProseMirror h1': { fontSize: '2xl', fontWeight: 'semibold', lineHeight: '8' },
            '.ProseMirror h2': { fontSize: 'xl', fontWeight: 'semibold', lineHeight: '7' },
            '.ProseMirror ul': { listStyleType: 'disc', paddingInlineStart: 6 },
            '.ProseMirror li + li': { mt: 1 },
            '.ProseMirror strong': { fontWeight: 'semibold' },
            '.ProseMirror a': { color: 'brand.500', textDecoration: 'underline' },
            '.email-variable': {
              bg: 'bg.subtle',
              borderRadius: 'sm',
              px: 1,
              fontSize: 'sm',
            },
            // The blocks outline themselves when selected; this covers a variable.
            '.ProseMirror-selectednode': { outline: '2px solid', outlineColor: 'brand.500' },
            // The gap caret between two blocks, invisible without this.
            '.ProseMirror-gapcursor': { position: 'relative', display: 'block' },
            '.ProseMirror-gapcursor:after': {
              content: '""',
              display: 'block',
              position: 'absolute',
              borderTop: '1px solid',
              borderColor: 'fg.default',
              width: pxToRem(20),
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Stack>

      {problem == null && bodyProblem == null ? null : (
        <Alert status="error" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription fontSize="sm">{bodyProblem ?? problem}</AlertDescription>
        </Alert>
      )}

      <Stack direction="row" gap={2}>
        <Button size="sm" onClick={save} isLoading={saving} data-testid={testIds.saveButton}>
          <Trans>Save</Trans>
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} isDisabled={saving}>
          <Trans>Cancel</Trans>
        </Button>
      </Stack>
    </Stack>
  );
}
